// Client-side cookie-consent source of truth (Poland RODO/GDPR).
//
// Categories: `necessary` (always on) + `analytics` (Google Analytics, opt-in).
// One cookie records the analytics decision so it survives a reload and resets
// if the visitor clears cookies:
//   'accepted' → analytics granted (GA may load)
//   'rejected' → analytics denied  (GA must not load; GA cookies are cleared)
//   absent     → no decision yet   (show the banner)

export const CONSENT_KEY = 'cookie-consent';

// Dispatched on `window` whenever the decision changes, so the analytics gate
// can mount/unmount GoogleAnalytics live — no page reload needed.
export const CONSENT_CHANGED_EVENT = 'cookie:consent-changed';

export type ConsentChoice = 'accepted' | 'rejected';

export function readConsent(): ConsentChoice | null {
  if (typeof document === 'undefined') return null;
  const entry = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${CONSENT_KEY}=`));
  if (!entry) return null;
  const value = entry.slice(CONSENT_KEY.length + 1);
  return value === 'accepted' || value === 'rejected' ? value : null;
}

export function hasDecision(): boolean {
  return readConsent() !== null;
}

export function analyticsAllowed(): boolean {
  return readConsent() === 'accepted';
}

// Persist the decision (1 year) and broadcast it. Denial also clears any GA
// cookies already dropped, so withdrawing consent actually removes them.
export function writeConsent(choice: ConsentChoice): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_KEY}=${choice};path=/;max-age=${365 * 24 * 60 * 60};SameSite=Lax`;
  if (choice === 'rejected') clearGaCookies();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice }));
}

// Remove Google Analytics cookies (_ga, _ga_*, _gid, _gat*) so a withdrawal
// leaves nothing behind. GA sets them on the registrable domain, so we expire
// each name against the host, the dot-host, and the dot-eTLD+1 plus a bare path.
export function clearGaCookies(): void {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split('; ')
    .map((c) => c.split('=')[0])
    .filter((n) => /^_ga/.test(n) || n === '_gid' || /^_gat/.test(n));
  if (names.length === 0) return;

  const host = window.location.hostname;
  const domains = [host, `.${host}`];
  const parts = host.split('.');
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);

  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=;path=/;domain=${domain};expires=${past}`;
    }
    document.cookie = `${name}=;path=/;expires=${past}`;
  }
}
